import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import Leopardy from "./Leopardy";
import LeopardyGames from "./LeopardyGames";
import LeopardyGameShell from "./LeopardyGameShell";
import DataViz from "./DataViz";
import Connections from "./Connections";
import Schmorg from "./Schmorg";
import NaNo from "./NaNo";
import Login from "./Login";
import RequireAuth from "./auth/RequireAuth";
import Babble from "./Babble";
import Profile from "./Profile";
import Toolbar from "./components/Toolbar";
import Wademon from "./Wademon";
import ChristmasCounter from "./ChristmasCounter";
import Oddventure from "./Oddventure";
import FoodPairing from "./FoodPairing";
import Cortisolace from "./Cortisolace";
import BinauralBeats from "./BinauralBeats";
import LifelapseFeature from "./LifelapseFeature";
import LifelapseNew from "./LifelapseNew";
import CultureClub from "./CultureClub";
import TOLOPOSOGO from "./TOLOPOSOGO";
import GraphExplorerPage from "./features/quiltGraph/pages/GraphExplorerPage";
import { QuiltFocusProvider } from "./features/quiltGraph/context/QuiltFocusContext";
import QuiltDeepLinkRedirect from "./features/quiltGraph/components/QuiltDeepLinkRedirect";
import { QuiltTimeProvider } from "./features/quiltGraph/time/timeContext";
import DocsListPage from "./features/docs/DocsListPage";
import DocEditorPage from "./features/docs/DocEditorPage";
import DeckListPage from "./features/srs/pages/DeckListPage";
import DeckDetailPage from "./features/srs/pages/DeckDetailPage";
import ImportDeckPage from "./features/srs/pages/ImportDeckPage";
import StudyDeckPage from "./features/srs/pages/StudyDeckPage";
import CardDetailPage from "./features/srs/pages/CardDetailPage";
import MasterStudyPage from "./features/srs/pages/MasterStudyPage";

if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  const roRe = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;
  const isResizeObserverNoise = (value) => roRe.test(String(value || ""));
  const originalConsoleError = window.console.error.bind(window.console);
  window.console.error = (...args) => {
    const text = args.map((arg) => (typeof arg === "string" ? arg : (arg?.message || ""))).join(" ");
    if (isResizeObserverNoise(text)) return;
    originalConsoleError(...args);
  };
  // Capture phase is important here because webpack dev overlay also listens to these events.
  const swallowResizeObserverError = (event) => {
    const message = event?.message || event?.error?.message || "";
    if (isResizeObserverNoise(message)) {
      event.preventDefault();
      if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
      return false;
    }
    return true;
  };
  window.addEventListener("error", swallowResizeObserverError, true);
  window.onerror = (message) => {
    if (isResizeObserverNoise(message)) return true;
    return false;
  };
  window.addEventListener("unhandledrejection", (event) => {
    const msg = event?.reason?.message || `${event?.reason || ""}`;
    if (isResizeObserverNoise(msg)) {
      event.preventDefault();
    }
  }, true);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <QuiltFocusProvider>
          <Toolbar />
          <Routes>
            <Route path="/" element={<App />} />
            <Route path="/leopardy" element={<Leopardy />} />
            <Route path="/leopardy/games" element={<RequireAuth><LeopardyGames /></RequireAuth>} />
            <Route path="/leopardy/host/:gameId" element={<RequireAuth><LeopardyGameShell role="host" /></RequireAuth>} />
            <Route path="/leopardy/play/:gameId" element={<RequireAuth><LeopardyGameShell role="player" /></RequireAuth>} />
            <Route path="/leopardy/view/:gameId" element={<LeopardyGameShell role="viewer" />} />
            <Route path="/dataviz" element={<DataViz />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/schmorg" element={<Schmorg />} />
            <Route path="/login" element={<Login />} />
            <Route path="/nanoking" element={<RequireAuth><NaNo /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
            <Route path="/babble" element={<Babble />} />
            <Route path="/babble/:id" element={<Babble />} />
            <Route path="/wademon" element={<Wademon />} />
            <Route path="/christmas-counter" element={<RequireAuth><ChristmasCounter /></RequireAuth>} />
            <Route path="/oddventure" element={<RequireAuth><Oddventure /></RequireAuth>} />
            <Route path="/oddventure/:tableId" element={<RequireAuth><Oddventure /></RequireAuth>} />
            <Route path="/food-pairing" element={<RequireAuth><FoodPairing /></RequireAuth>} />
            <Route path="/cortisolace" element={<Cortisolace />} />
            <Route path="/binaural-beats" element={<BinauralBeats />} />
            <Route path="/culture-club" element={<CultureClub />} />
            <Route path="/toloposogo" element={<TOLOPOSOGO />} />
            <Route path="/lifelapse" element={<LifelapseFeature />} />
            <Route path="/lifelapse/new" element={<LifelapseNew />} />
            <Route path="/graph" element={<RequireAuth><QuiltTimeProvider><GraphExplorerPage /></QuiltTimeProvider></RequireAuth>} />
            <Route path="/q/node/:id" element={<RequireAuth><QuiltDeepLinkRedirect kind="node" /></RequireAuth>} />
            <Route path="/q/edge/:id" element={<RequireAuth><QuiltDeepLinkRedirect kind="edge" /></RequireAuth>} />
            <Route path="/docs" element={<RequireAuth><DocsListPage /></RequireAuth>} />
            <Route path="/docs/:docId" element={<RequireAuth><DocEditorPage /></RequireAuth>} />
            <Route path="/srs" element={<RequireAuth><DeckListPage /></RequireAuth>} />
            <Route path="/srs/decks/:deckId" element={<RequireAuth><DeckDetailPage /></RequireAuth>} />
            <Route path="/srs/decks/:deckId/cards/:cardId" element={<RequireAuth><CardDetailPage /></RequireAuth>} />
            <Route path="/srs/decks/:deckId/import" element={<RequireAuth><ImportDeckPage /></RequireAuth>} />
            <Route path="/srs/decks/:deckId/study" element={<RequireAuth><StudyDeckPage /></RequireAuth>} />
            <Route path="/srs/master-study" element={<RequireAuth><MasterStudyPage /></RequireAuth>} />
          </Routes>
        </QuiltFocusProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
